import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ZIndex } from '@/consts/z-indexes.consts';
import { color } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { AppDialog } from '@/UI/AppDialog/AppDialog';
import { LabelSmRg, TextSmMd, TextSmRg } from '@/UI/AppTypography/AppTypography';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { type Option } from '@/UI/Dropdown/Dropdown';
import { Flex, FlexCol } from '@/UI/styles';
import FontsList, {
  BASE_BUILT_IN_FONT_KEY_SET,
} from '@/components/Recipe/FlowComponents/Editor/Designer/LayerPropertyPanel/FontsList';
import {
  decodeFontStyleValue,
  encodeFontStyleValue,
  type FontStyleCombination,
  type MissingCustomFontFamily,
} from '@/utils/customFonts.utils';
import { MissingFontFaceAccordionItemRow } from './MissingFontFaceAccordionItemRow';
import { MissingFontFamilyAccordionHeader } from './MissingFontFamilyAccordionHeader';
import { modalOutline, tableGridColumns } from './missingWorkflowFontsModal.styles';
import type { MissingFontFaceReplacement } from '@/types/missingWorkflowFonts.types';
import type { TFunction } from 'i18next';
import type { BuiltInFont } from 'web';
import type { FlowView } from '@/components/Recipe/Views/FlowView';

/** When every face in the family uses the same replacement built-in, return it (for header bulk control). */
function getCommonReplacementFontForFamily(
  fam: MissingCustomFontFamily,
  faceFont: Record<string, BuiltInFont>,
): BuiltInFont | null {
  const firstFace = fam.faces[0];
  if (!firstFace) {
    return null;
  }
  const candidate = faceFont[firstFace.faceKey];
  if (!candidate) {
    return null;
  }
  for (const face of fam.faces) {
    if (faceFont[face.faceKey] !== candidate) {
      return null;
    }
  }
  return candidate;
}

/** More than one distinct replacement font is chosen across faces in the same missing family. */
function hasMixedReplacementFontsForFamily(
  fam: MissingCustomFontFamily,
  faceFont: Record<string, BuiltInFont>,
): boolean {
  const distinct = new Set<BuiltInFont>();
  for (const face of fam.faces) {
    const b = faceFont[face.faceKey];
    if (b) {
      distinct.add(b);
      if (distinct.size > 1) {
        return true;
      }
    }
  }
  return false;
}

type MissingWorkflowFontsModalProps = {
  open: boolean;
  missingFamilies: MissingCustomFontFamily[];
  onReplace: (replacements: MissingFontFaceReplacement[]) => void;
  flowView: FlowView;
  /** Per `fontFamilyKey`, distinct faces in the user's active workspace uploads (drives company-font replacement styles). */
  workspaceFontStyleCombinationsByFamily: ReadonlyMap<string, FontStyleCombination[]>;
};

type MissingFontFamilyAccordionBlockProps = {
  fam: MissingCustomFontFamily;
  famIndex: number;
  expandedFamilyKeys: ReadonlySet<string>;
  toggleFamilyExpanded: (fontFamilyKey: string) => void;
  applyFamilyDefaultFontAndExpand: (fam: MissingCustomFontFamily, font: BuiltInFont) => void;
  faceFont: Record<string, BuiltInFont>;
  faceStyleValue: Record<string, string>;
  setFaceFont: Dispatch<SetStateAction<Record<string, BuiltInFont>>>;
  setFaceStyleValue: Dispatch<SetStateAction<Record<string, string>>>;
  combosByFontFamily: Map<BuiltInFont, FontStyleCombination[]>;
  fontOptions: Option<BuiltInFont>[];
  emptyFontPlaceholder: ReactNode;
  mixedFontPlaceholder: ReactNode;
  emptyStylePlaceholder: ReactNode;
  noOptionsText: string;
  t: TFunction;
};

function MissingFontFamilyAccordionBlock({
  fam,
  famIndex,
  expandedFamilyKeys,
  toggleFamilyExpanded,
  applyFamilyDefaultFontAndExpand,
  faceFont,
  faceStyleValue,
  setFaceFont,
  setFaceStyleValue,
  combosByFontFamily,
  fontOptions,
  emptyFontPlaceholder,
  mixedFontPlaceholder,
  emptyStylePlaceholder,
  noOptionsText,
  t,
}: MissingFontFamilyAccordionBlockProps) {
  const familyRegionId = `missing-font-family-${famIndex}`;
  const familyTriggerId = `missing-font-family-trigger-${famIndex}`;
  const isExpanded = expandedFamilyKeys.has(fam.fontFamilyKey);
  const commonHeaderFont = getCommonReplacementFontForFamily(fam, faceFont);
  const isReplacementMixed = hasMixedReplacementFontsForFamily(fam, faceFont);

  const onToggle = useCallback(() => {
    toggleFamilyExpanded(fam.fontFamilyKey);
  }, [fam.fontFamilyKey, toggleFamilyExpanded]);

  const onHeaderFontChange = useCallback(
    (font: BuiltInFont) => {
      applyFamilyDefaultFontAndExpand(fam, font);
    },
    [fam, applyFamilyDefaultFontAndExpand],
  );

  if (fam.faces.length === 0) {
    return null;
  }

  return (
    <FlexCol sx={{ borderBottom: modalOutline }}>
      <MissingFontFamilyAccordionHeader
        fontFamilyKey={fam.fontFamilyKey}
        isExpanded={isExpanded}
        familyRegionId={familyRegionId}
        familyTriggerId={familyTriggerId}
        onToggle={onToggle}
        commonHeaderFont={commonHeaderFont}
        isReplacementMixed={isReplacementMixed}
        fontOptions={fontOptions}
        emptyFontPlaceholder={emptyFontPlaceholder}
        mixedFontPlaceholder={mixedFontPlaceholder}
        noOptionsText={noOptionsText}
        onHeaderFontChange={onHeaderFontChange}
      />

      {isExpanded ? (
        <FlexCol id={familyRegionId} role="region" aria-labelledby={familyTriggerId}>
          {fam.faces.map((face) => (
            <MissingFontFaceAccordionItemRow
              key={face.faceKey}
              face={face}
              faceFont={faceFont}
              faceStyleValue={faceStyleValue}
              setFaceFont={setFaceFont}
              setFaceStyleValue={setFaceStyleValue}
              combosByFontFamily={combosByFontFamily}
              fontOptions={fontOptions}
              emptyFontPlaceholder={emptyFontPlaceholder}
              emptyStylePlaceholder={emptyStylePlaceholder}
              noOptionsText={noOptionsText}
              t={t}
            />
          ))}
        </FlexCol>
      ) : null}
    </FlexCol>
  );
}

export function MissingWorkflowFontsModal({
  open,
  missingFamilies,
  onReplace,
  flowView,
  workspaceFontStyleCombinationsByFamily,
}: MissingWorkflowFontsModalProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const baseFontStyleCombinationsCacheRef = useRef(new Map<BuiltInFont, FontStyleCombination[]>());
  const getBaseFontStyleCombinations = useCallback(
    (fontfamiliyKey: BuiltInFont) => {
      const cache = baseFontStyleCombinationsCacheRef.current;
      const cached = cache.get(fontfamiliyKey);
      if (cached) {
        return cached;
      }
      const result = flowView.getBaseFontStyleCombinations(fontfamiliyKey);
      cache.set(fontfamiliyKey, result);
      return result;
    },
    [flowView],
  );
  const [expandedFamilyKeys, setExpandedFamilyKeys] = useState<ReadonlySet<string>>(() => new Set());
  const [faceFont, setFaceFont] = useState<Record<string, BuiltInFont>>({});
  const [faceStyleValue, setFaceStyleValue] = useState<Record<string, string>>({});

  const toggleFamilyExpanded = useCallback((fontFamilyKey: string) => {
    setExpandedFamilyKeys((prev) => {
      const next = new Set(prev);
      if (next.has(fontFamilyKey)) {
        next.delete(fontFamilyKey);
      } else {
        next.add(fontFamilyKey);
      }
      return next;
    });
  }, []);

  /** Sets the same replacement built-in on every face in the family, clears styles, and expands the row. */
  const applyFamilyDefaultFontAndExpand = useCallback((fam: MissingCustomFontFamily, fontfamiliyKey: BuiltInFont) => {
    setFaceFont((prev) => {
      const next = { ...prev };
      for (const face of fam.faces) {
        next[face.faceKey] = fontfamiliyKey;
      }
      return next;
    });
    setFaceStyleValue((prev) => {
      const next = { ...prev };
      for (const face of fam.faces) {
        delete next[face.faceKey];
      }
      return next;
    });
    setExpandedFamilyKeys((prev) => new Set(prev).add(fam.fontFamilyKey));
  }, []);

  /** Stable fingerprint of missing faces so we reset picks when the list changes, not only when the array reference changes. */
  const familiesSignature = useMemo(
    () => missingFamilies.map((f) => `${f.fontFamilyKey}:${f.faces.map((x) => x.faceKey).join(',')}`).join('|'),
    [missingFamilies],
  );

  // New missing-font snapshot (familiesSignature): discard prior selections and collapse rows.
  useEffect(() => {
    setFaceFont({});
    setFaceStyleValue({});
    setExpandedFamilyKeys(new Set());
  }, [familiesSignature]);

  const fontOptions = useMemo<Option<BuiltInFont>[]>(() => {
    const workspaceFamilyKeys = new Set(workspaceFontStyleCombinationsByFamily.keys());
    return FontsList()
      .filter((f) => BASE_BUILT_IN_FONT_KEY_SET.has(f.value) || workspaceFamilyKeys.has(f.value))
      .map((f) => ({
        value: f.value,
        label: f.label,
        id: f.value,
      }));
  }, [workspaceFontStyleCombinationsByFamily]);

  /** Distinct replacement fonts currently chosen anywhere in the table; drives which style combo lists we build. */
  const fontsToQuery = useMemo(() => {
    const s = new Set<BuiltInFont>();
    for (const fam of missingFamilies) {
      for (const face of fam.faces) {
        const b = faceFont[face.faceKey];
        if (b) {
          s.add(b);
        }
      }
    }
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [missingFamilies, faceFont]);

  /** Legal weight/italic pairs per replacement font: built-ins from the engine helper, uploaded families from workspace metadata. */
  const combosByFontFamily = useMemo(() => {
    const m = new Map<BuiltInFont, FontStyleCombination[]>();
    for (const fontfamiliyKey of fontsToQuery) {
      const combos = BASE_BUILT_IN_FONT_KEY_SET.has(fontfamiliyKey)
        ? getBaseFontStyleCombinations(fontfamiliyKey)
        : (workspaceFontStyleCombinationsByFamily.get(fontfamiliyKey) ?? []);
      m.set(fontfamiliyKey, combos);
    }
    return m;
  }, [fontsToQuery, getBaseFontStyleCombinations, workspaceFontStyleCombinationsByFamily]);

  /**
   * When the replacement font exposes exactly one style, lock it in so the row is complete without opening the style dropdown.
   * (Keeps `allFacesComplete` and Replace in sync when there is no real choice.)
   */
  useEffect(() => {
    if (!open) {
      return;
    }
    setFaceStyleValue((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const fam of missingFamilies) {
        for (const face of fam.faces) {
          const b = faceFont[face.faceKey];
          if (!b) {
            continue;
          }
          const combos = combosByFontFamily.get(b);
          if (!combos || combos.length !== 1) {
            continue;
          }
          const enc = encodeFontStyleValue(combos[0].weight, combos[0].isItalic);
          if (next[face.faceKey] !== enc) {
            next[face.faceKey] = enc;
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
  }, [open, missingFamilies, faceFont, combosByFontFamily]);

  /** Every missing face must have a replacement font and a decodable style before Replace is enabled. */
  const allFacesComplete = useMemo(() => {
    for (const fam of missingFamilies) {
      for (const face of fam.faces) {
        const b = faceFont[face.faceKey];
        if (!b) {
          return false;
        }
        const style = faceStyleValue[face.faceKey];
        if (!style || !decodeFontStyleValue(style)) {
          return false;
        }
      }
    }
    return missingFamilies.length > 0;
  }, [missingFamilies, faceFont, faceStyleValue]);

  const handleReplace = useCallback(() => {
    const list: MissingFontFaceReplacement[] = [];
    for (const fam of missingFamilies) {
      for (const face of fam.faces) {
        const b = faceFont[face.faceKey];
        const raw = faceStyleValue[face.faceKey];
        const decoded = raw ? decodeFontStyleValue(raw) : null;
        // Defensive: button is disabled unless complete, but bail instead of emitting a partial payload.
        if (!b || !decoded) {
          return;
        }
        list.push({
          sourceFaceKey: face.faceKey,
          replacementFontFamilyKey: b,
          replacementWeight: decoded.weight,
          replacementItalic: decoded.isItalic,
        });
      }
    }
    onReplace(list);
  }, [missingFamilies, faceFont, faceStyleValue, onReplace]);

  const emptyFontPlaceholder = useMemo(
    () => (
      <TextSmRg component="span" sx={{ color: color.White64_T }}>
        {t(I18N_KEYS.COMPOSITOR.MISSING_WORKSPACE_FONTS_MODAL.SELECT_FONT)}
      </TextSmRg>
    ),
    [t],
  );

  const mixedFontPlaceholder = useMemo(
    () => (
      <TextSmRg component="span" sx={{ color: color.White64_T }}>
        {t(I18N_KEYS.COMPOSITOR.MISSING_WORKSPACE_FONTS_MODAL.MIXED_REPLACEMENT_FONT)}
      </TextSmRg>
    ),
    [t],
  );

  const emptyStylePlaceholder = useMemo(
    () => (
      <TextSmRg component="span" sx={{ color: color.White64_T }}>
        {t(I18N_KEYS.COMPOSITOR.MISSING_WORKSPACE_FONTS_MODAL.SELECT_STYLE)}
      </TextSmRg>
    ),
    [t],
  );

  const fontSelectNoOptionsText = useMemo(
    () => t(I18N_KEYS.COMPOSITOR.LAYER_PROPERTY_PANEL.FONT_SELECT_INPUT.NO_OPTIONS_TEXT),
    [t],
  );

  const handleDialogClose = useCallback<NonNullable<ComponentProps<typeof AppDialog>['onClose']>>((_, reason) => {
    // Modal is intentionally non-dismissible via backdrop/Escape; parent controls `open` (e.g. after Replace or app flow).
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
  }, []);

  return (
    <AppDialog
      open={open}
      onClose={handleDialogClose}
      aria-labelledby={titleId}
      disableEscapeKeyDown
      fullWidth
      maxWidth="sm"
      sx={{
        zIndex: ZIndex.Overlays.CONFIRMATION_DIALOGS,
        '& .MuiDialog-container': {
          alignItems: 'flex-start',
          paddingTop: '16px',
        },
      }}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'transparent' },
        },
        paper: {
          sx: {
            background: color.Black92,
            borderRadius: '8px',
            border: modalOutline,
            maxWidth: 560,
            maxHeight: '360px',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 12px 40px rgba(14, 14, 19, 0.18)',
            overflow: 'scroll',
            /** Dialog theme default adds vertical margin; keep flush below container padding so top offset is exactly 16px. */
            m: '0 auto',
          },
        },
      }}
    >
      <FlexCol sx={{ width: '100%', minWidth: 0, flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}>
        <Flex
          id={titleId}
          sx={{
            px: 2,
            pr: 1.5,
            py: 1.5,
            borderBottom: modalOutline,
            minHeight: 48,
            boxSizing: 'border-box',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <LabelSmRg component="span" sx={{ color: color.White80_T, display: 'block' }}>
            {t(I18N_KEYS.COMPOSITOR.MISSING_WORKSPACE_FONTS_MODAL.TITLE)}
          </LabelSmRg>
        </Flex>
        <FlexCol sx={{ pt: 0, px: 2, pr: 1, pb: 0, flex: '1 1 auto', minHeight: 0, overflow: 'auto' }}>
          <Flex
            sx={{
              display: 'grid',
              gridTemplateColumns: tableGridColumns,
              alignItems: 'center',
              columnGap: 2,
              minHeight: 40,
              boxSizing: 'border-box',
              py: 0.5,
              borderBottom: modalOutline,
            }}
          >
            <TextSmMd sx={{ color: color.White100, lineHeight: '15.4px' }}>
              {t(I18N_KEYS.COMPOSITOR.MISSING_WORKSPACE_FONTS_MODAL.COLUMN_MISSING)}
            </TextSmMd>
            <Flex aria-hidden sx={{ width: 16, minWidth: 16 }} />
            <TextSmMd sx={{ color: color.White100, lineHeight: '15.4px' }}>
              {t(I18N_KEYS.COMPOSITOR.MISSING_WORKSPACE_FONTS_MODAL.COLUMN_REPLACEMENT)}
            </TextSmMd>
          </Flex>

          {missingFamilies.map((fam, famIndex) => (
            <MissingFontFamilyAccordionBlock
              key={fam.fontFamilyKey}
              fam={fam}
              famIndex={famIndex}
              expandedFamilyKeys={expandedFamilyKeys}
              toggleFamilyExpanded={toggleFamilyExpanded}
              applyFamilyDefaultFontAndExpand={applyFamilyDefaultFontAndExpand}
              faceFont={faceFont}
              faceStyleValue={faceStyleValue}
              setFaceFont={setFaceFont}
              setFaceStyleValue={setFaceStyleValue}
              combosByFontFamily={combosByFontFamily}
              fontOptions={fontOptions}
              emptyFontPlaceholder={emptyFontPlaceholder}
              mixedFontPlaceholder={mixedFontPlaceholder}
              emptyStylePlaceholder={emptyStylePlaceholder}
              noOptionsText={fontSelectNoOptionsText}
              t={t}
            />
          ))}
        </FlexCol>
        <Flex
          sx={{ px: 2, pr: 1.5, py: 1.5, borderTop: modalOutline, alignItems: 'center', width: '100%', flexShrink: 0 }}
        >
          <Flex sx={{ flex: 1 }} />
          <ButtonContained
            size="xs"
            mode="filled-light"
            disabled={!allFacesComplete}
            onClick={handleReplace}
            sx={{ borderRadius: '4px', textTransform: 'none', fontWeight: 500, lineHeight: '15.4px' }}
          >
            {t(I18N_KEYS.COMPOSITOR.MISSING_WORKSPACE_FONTS_MODAL.REPLACE)}
          </ButtonContained>
        </Flex>
      </FlexCol>
    </AppDialog>
  );
}
